import { AfterViewInit, Component, ChangeDetectionStrategy, inject } from "@angular/core";
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpRequestResponse } from "../../../lib/models/http-request-response.model";
import { ApplicationService } from "../../../lib/services/application.service";
import { HttpRequestService } from "../../../lib/services/http-request.service";
import { LogService } from "../../../lib/services/log.service";
import { UtenteService } from "../../../lib/services/utente.service";
import { newError } from "../../../lib/utils/Utility";
import { MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent, MatCardActions } from "@angular/material/card";
import { EvLogoComponent } from "../../../lib/components/ev-logo.component";
import { MatFormField, MatLabel, MatInput, MatSuffix } from "@angular/material/input";
import { MatIcon } from "@angular/material/icon";
import { MatButton } from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";
import { ScaricaAppComponent } from "./scarica-app.component";
import { BiometricAuthService } from "../../../lib/services/biometric-auth.service";
import { environment } from "../../../../environments/environment";

@Component({
    selector: 'login-portale',
    template: `
<mat-card class="portal-login-card">
  <mat-card-header>
    <mat-card-title>{{applicationService.getApplicationName()}} <span>- Login</span></mat-card-title>
    <mat-card-subtitle>Il portale per la comunicazione Medico - Paziente</mat-card-subtitle>
  </mat-card-header>

  <mat-card-content>

    <form [formGroup]="form">
      <ev-logo></ev-logo>

      <mat-form-field style="width: 100%;">
        <mat-label>Nome utente</mat-label>
        <input matInput formControlName="username" style="text-transform: lowercase;">
      </mat-form-field>

      <mat-form-field style="width: 100%;">
        <mat-label>Password</mat-label>
        <input matInput type="password" formControlName="password" #pwd>
        <mat-icon matSuffix (click)="togglePassword(pwd)">visibility</mat-icon>
      </mat-form-field>

      @if (biometricSupported) {
        <mat-checkbox formControlName="abilitaBiometria">
          Abilita l'accesso con impronta o riconoscimento del dispositivo
        </mat-checkbox>
      }
    </form>

  </mat-card-content>
  <mat-card-actions class="portal-login-card__actions">
    <button mat-raised-button color="primary" [disabled]="!form.valid" (click)="login()">ACCEDI</button>
    @if (biometricSupported) {
      <button mat-stroked-button color="primary" [disabled]="!username || loginBiometricoInCorso" (click)="loginBiometrico()">
        <mat-icon>fingerprint</mat-icon>
        ACCEDI CON BIOMETRIA
      </button>
    }
  </mat-card-actions>

  <div class="portal-login-card__recovery">
    @if (mostraRecuperoPin) {
      <a  style="text-decoration: none; font-size: 12px !important"
        (click)="router.navigate(['/impostazioni/forgot_pin']);">
      Utente dimenticato?</a>
    }

    @if (mostraRecuperoPassword) {
      <a style="text-decoration: none; font-size: 12px !important"
        (click)="router.navigate(['/impostazioni/forgot_password'])">
      Password dimenticata?</a>
    }
  </div>

  <app-scarica-app-section [showHeader]="false"></app-scarica-app-section>

  <div class="portal-login-card__video">
    <button mat-raised-button color="primary" (click)="router.navigate(['accesso_videocall'])">
      <mat-icon style="font-size: 15px">video_call</mat-icon>
      ACCEDI PER TELEVISITA
    </button>

  </div>
</mat-card>
`,
    styles: [
        `
        app-scarica-app-section ::ng-deep div {
            background-color: transparent !important;
            padding: 0 !important;
        }
        app-scarica-app-section ::ng-deep .app-download-section {
            padding: 14px 20px !important;
        }
        `,
        `
        .portal-login-card { background: rgba(255, 255, 255, .96); border: 1px solid rgba(255, 255, 255, .72); border-radius: 18px; box-shadow: 0 22px 50px rgba(10, 53, 48, .32); margin: 0 auto; max-width: 420px; padding: 28px; width: 100%; }
        .portal-login-card mat-card-header { display: block; margin-bottom: 12px; text-align: center; }
        .portal-login-card mat-card-title { color: #1d4e49; font-size: 24px; font-weight: 700 !important; }
        .portal-login-card mat-card-title span { color: #5b7774; font-weight: 400 !important; }
        .portal-login-card mat-card-subtitle { color: #5b7774; margin-top: 7px; }
        .portal-login-card__actions { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; margin: 8px 0 0; }
        .portal-login-card__recovery { align-items: center; display: flex; flex-direction: column; gap: 8px; margin-top: 20px; }
        .portal-login-card__recovery a { color: #216961; cursor: pointer; font-size: 13px !important; text-decoration: none; }
        .portal-login-card__recovery a:hover { text-decoration: underline; }
        .portal-login-card__video { display: flex; justify-content: center; margin-top: 28px; }
        @media (max-width: 500px) { .portal-login-card { border-radius: 15px; padding: 22px 18px; } }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatCard, MatCardHeader, MatCardTitle, MatCardSubtitle, MatCardContent, ReactiveFormsModule, EvLogoComponent, MatFormField, MatLabel, MatInput, MatIcon, MatSuffix, MatCardActions, MatButton, MatCheckbox, ScaricaAppComponent]
})
export class LoginPortaleComponent implements AfterViewInit {
    private utenteService = inject(UtenteService);
    private httpRequestService = inject(HttpRequestService);
    protected router = inject(Router);
    protected applicationService = inject(ApplicationService);
    private log = inject(LogService);
    private activatedRoute = inject(ActivatedRoute);
    private titleService = inject(Title);
    private biometricAuthService = inject(BiometricAuthService);

    mostraRecuperoPin = false
    mostraRecuperoPassword = false

    form = new FormGroup({
        username: new FormControl('', [Validators.required]),
        password: new FormControl('', [Validators.required]),
        abilitaBiometria: new FormControl(false),
    })

    biometricSupported = false
    loginBiometricoInCorso = false

    get username() {
        return this.form.controls.username.value.toLowerCase()
    }
    get password() {
        return this.form.controls.password.value
    }

    isNc: boolean

    constructor() {
        // resetto la navigazione, altrimenti se l'utente non cambia pass al primo accesso non riesce più ad entrare
        localStorage.removeItem('saluteconte.no-nav')

        this.isNc = this.activatedRoute.snapshot.queryParams['nc'] === 'true'
        let appName

        if (this.isNc) {
            appName = 'NEXT Comunica'
        } else {
            appName = 'Salute con te'
        }

        localStorage.setItem('saluteconte.nome-applicazione', appName)

        this.titleService.setTitle(appName)
        this.biometricSupported = environment.config.biometricLogin.enabled && this.biometricAuthService.isSupported()

        // gestione login silente (usato dal vecchio sct)
        const accessToken = this.activatedRoute.snapshot.queryParams['t'] 
        const refreshToken = this.activatedRoute.snapshot.queryParams['r'] 
        
        if(accessToken) {
            localStorage.setItem('saluteconte.token', accessToken);
            localStorage.setItem('saluteconte.refresh_token', refreshToken);
            this.utenteService.setBypassLogin(true)
        }
    }

    togglePassword(ctrl) {
        ctrl.type = ctrl.type === 'password' ? 'text' : 'password'
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            // prima di tutto verifico se ho un token valido nello storage del browser. in caso affermativo
            // vado al check della policy
            if (this.utenteService.isBypassLogin()) {
                this.utenteService.setBypassLogin(false)

                this.httpRequestService.get('/login/check-token').subscribe(response => {
                    if (HttpRequestResponse.isSuccessResponse(response)) {
                        this.utenteService.getDatiUtente().then(() => {
                            const username = this.utenteService.getChiave(['paziente', 'pin'])
                            if (username) {
                                this.isNormativaPrivacyAccettata(username)
                            }
                        })
                    }
                })
            }
        }, 100)


    }

    isNormativaPrivacyAccettata(username?: string) {
        this.httpRequestService.get(`/privacy-policy/user/${username || this.username}`, { isNc: this.applicationService.isNextComunica() }).subscribe((response) => {
            if (HttpRequestResponse.isSuccessResponse(response)) {

                this.utenteService.pulisciDatiUtente()
                this.utenteService.getDatiUtente().then(() => {
                    //Se la normativa privacy non è stata mai accettata reindirizzo L'utente alla pagina di accettazione
                    if (response?.data?.fattoPrimoAccesso !== "S") {
                        this.router.navigate(['/normativaPrivacy'], {})
                        return
                    }
                    // verifico se l'utente deve andare al form di pagamento
                    this.checkAbbonamentoScaduto();
                })
            } else {
                this.log.messageError("Errore", response.message, newError(response.message))
            }
        });
    }

    checkAbbonamentoScaduto() {
        if (this.utenteService.mustPay()) {
            this.router.navigate(['impostazioni', 'dati-fatturazione', 'abbonamento-scaduto'], {});
        } else {
            //Se non è scaduto l'abbonamento prima di loggarmi all'applicazione controllo che la password non sia scaduta, e quindi debba essere reimpostata
            this.checkPasswordScaduta()
        }
    }

    checkPasswordScaduta() {
        this.httpRequestService.get('/siev_fdatamod_password/password/check/expired/', {}).subscribe((response) => {
            if (HttpRequestResponse.isSuccessResponse(response)) {

                if (response.data?.isPasswordScaduta) {
                    this.router.navigate(['/cambioPassword'], { queryParams: { f: 1 }, });
                } else {
                    this.router.navigate(['/homepage'], {});
                }
            } else {
                this.log.messageError("Errore", response.message, newError(response.message))
            }
        });
    }

    login() {
        this.mostraRecuperoPin = false
        this.mostraRecuperoPassword = false

        this.httpRequestService.post('/login', {
            pin: this.username,
            password: this.password,
            isNc: this.isNc
        }).subscribe(async (response) => {
            if (HttpRequestResponse.isSuccessResponse(response)) {
                this.salvaToken(response.data.tokens)

                if (this.form.controls.abilitaBiometria.value && this.biometricSupported) {
                    try {
                        await this.biometricAuthService.register()
                        this.log.messageInfo("Accesso biometrico", "Accesso con biometria attivato")
                    } catch (err) {
                        this.log.messageWarning("Accesso biometrico", this.biometricErrorMessage(err))
                    }
                }

                await this.completaLogin()

            } else {
                this.log.messageWarning("Attenzione", response.message)
                
                if(!(response?.data?.isPinEsistente)) {
                    this.mostraRecuperoPin = true
                    return
                }
                if(!(response?.data?.isPasswordValida)) {
                    this.mostraRecuperoPassword = true
                    return
                }
            }
        });
    }

    async loginBiometrico() {
        this.loginBiometricoInCorso = true
        try {
            const tokens = await this.biometricAuthService.authenticate(this.username)
            this.salvaToken(tokens)
            await this.completaLogin()
        } catch (err) {
            this.log.messageWarning("Accesso biometrico", this.biometricErrorMessage(err))
        } finally {
            this.loginBiometricoInCorso = false
        }
    }

    private salvaToken(tokens: { access_token: string, refresh_token: string }) {
        this.utenteService.setDatiUtente(tokens)
        localStorage.setItem('saluteconte.token', tokens.access_token)
        localStorage.setItem('saluteconte.refresh_token', tokens.refresh_token)
    }

    private async completaLogin() {
        await this.utenteService.getDatiUtente()
        this.isNormativaPrivacyAccettata()
    }

    private biometricErrorMessage(err: any): string {
        if (err?.name === 'NotAllowedError') {
            return "Operazione annullata o verifica del dispositivo non riuscita"
        }
        return err?.message || "Accesso biometrico non riuscito"
    }

    /* verificaPrimoAccesso() {
        const pUsername = this.username.toLowerCase();

        this.httpRequestService
            .post('/siev_fdatamod_password', {
                pin: pUsername
            })
            .subscribe((response) => {
                if (HttpRequestResponse.isSuccessResponse(response)) {
                    //Ottengo la data di modifica della password
                    this.dataModificaPassword = response.data?.dataModificaPassword

                    //se la password è stata modificata in passato setto le variabili sul primo accesso true sul primo accesso false se invece non è cosi faccio il contrario
                    if (this.dataModificaPassword) {
                        this.primoAccessoEseguito = true
                        this.primoAccessoNonEseguito = false
                    } else {
                        this.primoAccessoEseguito = false
                        this.primoAccessoNonEseguito = true
                    }
                } else {

                }
            });

    } */
}
